import { useCallback, useEffect, useState } from 'react'

import { Delivery, DeliveryLog } from './Delivery'
import {
  api,
  fileToBase64,
  type ApiKey,
  type AppDetail as AppDetailData,
  type Diagnostics,
  type NotificationRow,
  type Stats,
  type Subscription,
} from '../api'

type Tab = 'overview' | 'keys' | 'channels' | 'audience' | 'messages' | 'delivery' | 'log' | 'diagnostics'

const TABS: { id: Tab; title: string }[] = [
  { id: 'overview', title: 'Обзор' },
  { id: 'messages', title: 'Рассылки' },
  { id: 'delivery', title: 'Доставка' },
  { id: 'audience', title: 'Аудитория' },
  { id: 'log', title: 'Журнал' },
  { id: 'channels', title: 'Платформы' },
  { id: 'keys', title: 'Ключи и ID' },
  { id: 'diagnostics', title: 'Диагностика' },
]

export function AppDetail({
  appId,
  onBack,
  onAppIdChanged,
}: {
  appId: string
  onBack: () => void
  onAppIdChanged: (id: string) => void
}) {
  const [tab, setTab] = useState<Tab>('overview')
  const [app, setApp] = useState<AppDetailData | null>(null)
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    try {
      setApp(await api.app(appId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить приложение')
    }
  }, [appId])

  useEffect(() => {
    void reload()
  }, [reload])

  if (!app) {
    return <div className="page">{error ? <div className="notice error">{error}</div> : 'Загрузка…'}</div>
  }

  return (
    <div className="stack">
      <div>
        <button className="link" onClick={onBack}>
          ← Все приложения
        </button>
        <h1>{app.name}</h1>
        <p className="muted mono">{app.id}</p>
      </div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.id} className={tab === t.id ? 'active' : ''} onClick={() => setTab(t.id)}>
            {t.title}
          </button>
        ))}
      </div>

      {error && <div className="notice error">{error}</div>}

      {tab === 'overview' && <Overview appId={appId} />}
      {tab === 'messages' && <Messages appId={appId} />}
      {tab === 'delivery' && <Delivery appId={appId} />}
      {tab === 'log' && <DeliveryLog appId={appId} />}
      {tab === 'audience' && <Audience appId={appId} />}
      {tab === 'channels' && <Channels app={app} onSaved={reload} />}
      {tab === 'keys' && <Keys app={app} onAppIdChanged={onAppIdChanged} onDeleted={onBack} />}
      {tab === 'diagnostics' && <DiagnosticsTab appId={appId} />}
    </div>
  )
}

function Overview({ appId }: { appId: string }) {
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    api.stats(appId).then(setStats).catch(() => undefined)
  }, [appId])

  const totals = { sent: 0, clicked: 0, failed: 0 }
  for (const point of stats?.daily ?? []) {
    if (point.type === 'sent') totals.sent += point.count
    if (point.type === 'clicked') totals.clicked += point.count
    if (point.type === 'failed' || point.type === 'dropped') totals.failed += point.count
  }
  const ctr = totals.sent > 0 ? ((totals.clicked / totals.sent) * 100).toFixed(1) : '0.0'

  return (
    <div className="stack">
      <div className="grid">
        <Tile label="Подписки" value={stats?.audience.subscribed ?? 0} />
        <Tile label="Всего устройств" value={stats?.audience.total ?? 0} />
        <Tile label="Отправлено за 14 дней" value={totals.sent} />
        <Tile label="CTR" value={`${ctr}%`} />
      </div>
      {(stats?.audience.invalid ?? 0) > 0 && (
        <div className="notice">
          Мёртвых токенов: {stats?.audience.invalid}. Они погашены автоматически после ответа провайдера.
        </div>
      )}
    </div>
  )
}

function Tile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="card tile">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  )
}

function Messages({ appId }: { appId: string }) {
  const [rows, setRows] = useState<NotificationRow[]>([])
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [audience, setAudience] = useState('Subscribed Users')
  const [externalIDs, setExternalIDs] = useState('')
  const [result, setResult] = useState('')
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    const { notifications } = await api.notifications(appId)
    setRows(notifications ?? [])
  }, [appId])

  useEffect(() => {
    void reload().catch(() => undefined)
  }, [reload])

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setError('')
    setResult('')
    try {
      const payload: Record<string, unknown> = { title, body }
      if (audience === 'external') {
        payload.external_ids = externalIDs.split(/[\s,]+/).filter(Boolean)
      } else {
        payload.segment = audience
      }
      const response = await api.sendPush(appId, payload)
      setResult(
        response.recipients > 0
          ? `Рассылка ${response.id} поставлена в очередь, получателей: ${response.recipients}`
          : `Рассылка создана, но подходящих подписок нет`,
      )
      setTitle('')
      setBody('')
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить')
    }
  }

  return (
    <div className="stack">
      <form className="card stack" onSubmit={send}>
        <h2>Новый пуш</h2>
        <div className="grid">
          <label>
            Заголовок
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Заголовок" />
          </label>
          <label>
            Аудитория
            <select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="Subscribed Users">Все подписанные</option>
              <option value="Active Users">Активные за 30 дней</option>
              <option value="Engaged Users">Вовлечённые</option>
              <option value="external">По external_id</option>
            </select>
          </label>
        </div>
        <label>
          Текст
          <textarea required rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
        </label>
        {audience === 'external' && (
          <label>
            external_id через запятую
            <input value={externalIDs} onChange={(e) => setExternalIDs(e.target.value)} />
          </label>
        )}
        {error && <div className="notice error">{error}</div>}
        {result && <div className="notice">{result}</div>}
        <div className="row">
          <button className="primary" type="submit">
            Отправить
          </button>
        </div>
      </form>

      <div className="card table-wrap">
        <h2>История</h2>
        <table>
          <thead>
            <tr>
              <th>Сообщение</th>
              <th>Статус</th>
              <th>Аудитория</th>
              <th>Доставлено</th>
              <th>Открыто</th>
              <th>Ошибки</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.id}>
                <td>
                  <strong>{n.headings?.en ?? '—'}</strong>
                  <div className="muted">{n.contents?.en}</div>
                </td>
                <td>
                  <span className={`badge ${n.status === 'completed' ? 'ok' : ''}`}>{n.status}</span>
                </td>
                <td>{n.recipients}</td>
                <td>{n.successful}</td>
                <td>{n.converted}</td>
                <td>{n.failed + n.errored}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Рассылок пока не было.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Audience({ appId }: { appId: string }) {
  const [rows, setRows] = useState<Subscription[]>([])
  const [message, setMessage] = useState('')
  const [openDevice, setOpenDevice] = useState<string | null>(null)

  useEffect(() => {
    api.subscriptions(appId).then((r) => setRows(r.subscriptions ?? [])).catch(() => undefined)
  }, [appId])

  async function test(subscriptionId: string) {
    try {
      await api.testPush(appId, subscriptionId, 'PushMe', 'Тестовое уведомление')
      setMessage('Тестовый пуш поставлен в очередь')
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Не удалось отправить')
    }
  }

  return (
    <div className="stack">
      {message && <div className="notice">{message}</div>}
      <div className="card table-wrap">
        <table>
          <thead>
            <tr>
              <th>Устройство</th>
              <th>Платформа</th>
              <th>external_id</th>
              <th>Пушей за 30д</th>
              <th>Последний пуш</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id}>
                <td>
                  <button className="link mono" onClick={() => setOpenDevice(openDevice === s.id ? null : s.id)}>
                    {s.id.slice(0, 8)}…
                  </button>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {s.device_model || '—'} {s.device_os}
                  </div>
                </td>
                <td>{s.type}</td>
                <td>{s.external_id || '—'}</td>
                <td>
                  {s.sent_30d}
                  {s.clicked_30d > 0 && <span className="muted"> · открыто {s.clicked_30d}</span>}
                  {s.failed_30d > 0 && <span className="muted"> · ошибок {s.failed_30d}</span>}
                </td>
                <td className="muted">
                  {s.last_sent_at ? new Date(s.last_sent_at).toLocaleString('ru-RU') : '—'}
                </td>
                <td>
                  {s.invalid_at ? (
                    <span className="badge bad">мёртвый токен</span>
                  ) : s.enabled && s.notification_types > 0 ? (
                    <span className="badge ok">подписан</span>
                  ) : (
                    <span className="badge">отписан</span>
                  )}
                </td>
                <td>
                  <button className="link" onClick={() => test(s.id)}>
                    Тестовый пуш
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  Устройств пока нет. Подключите SDK и запустите приложение.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openDevice && (
        <div className="stack">
          <div className="row">
            <h2 style={{ flex: 1, margin: 0 }}>История пушей устройства</h2>
            <button className="link" onClick={() => setOpenDevice(null)}>
              Свернуть
            </button>
          </div>
          <DeliveryLog appId={appId} subscriptionId={openDevice} />
        </div>
      )}
    </div>
  )
}

function Channels({ app, onSaved }: { app: AppDetailData; onSaved: () => void }) {
  return (
    <div className="stack">
      <AndroidChannel app={app} onSaved={onSaved} />
      <IOSChannel app={app} onSaved={onSaved} />
      <WebChannel app={app} onSaved={onSaved} />
      <HuaweiChannel app={app} onSaved={onSaved} />
    </div>
  )
}

function channelOf(app: AppDetailData, name: string) {
  return app.channels?.find((c) => c.channel === name)
}

function AndroidChannel({ app, onSaved }: { app: AppDetailData; onSaved: () => void }) {
  const existing = channelOf(app, 'android')
  const [senderID, setSenderID] = useState(existing?.config?.sender_id ?? '')
  const [apiKey, setApiKey] = useState(existing?.config?.fcm_api_key ?? '')
  const [appIDValue, setAppIDValue] = useState(existing?.config?.fcm_app_id ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [status, setStatus] = useState('')

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setStatus('Сохраняю…')
    try {
      const payload: Record<string, unknown> = {
        enabled: true,
        config: { sender_id: senderID, fcm_api_key: apiKey, fcm_app_id: appIDValue },
        validate: Boolean(file),
      }
      if (file) {
        payload.credential = { kind: 'fcm_sa', data_base64: await fileToBase64(file) }
      }
      await api.saveChannel(app.id, 'android', payload)
      setStatus('Сохранено')
      onSaved()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Ошибка сохранения')
    }
  }

  return (
    <form className="card stack" onSubmit={save}>
      <h2>Android — FCM HTTP v1</h2>
      <p className="muted">
        Service account используется для отправки. Поля из google-services.json уходят в SDK
        через android_params.js — без них устройство не получит FCM-токен.
      </p>
      <div className="grid">
        <label>
          Sender ID
          <input value={senderID} onChange={(e) => setSenderID(e.target.value)} placeholder="123456789012" />
        </label>
        <label>
          API key
          <input value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="AIza…" />
        </label>
        <label>
          Mobile SDK App ID
          <input
            value={appIDValue}
            onChange={(e) => setAppIDValue(e.target.value)}
            placeholder="1:123456789012:android:…"
          />
        </label>
        <label>
          Service account JSON
          <input type="file" accept="application/json" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
      </div>
      <CredentialState channel={existing} />
      <div className="row">
        <button className="primary" type="submit">
          Сохранить
        </button>
        {status && <span className="muted">{status}</span>}
      </div>
    </form>
  )
}

function IOSChannel({ app, onSaved }: { app: AppDetailData; onSaved: () => void }) {
  const existing = channelOf(app, 'ios')
  const [bundleID, setBundleID] = useState(existing?.config?.bundle_id ?? '')
  const [env, setEnv] = useState(existing?.config?.default_env ?? 'production')
  const [pushType, setPushType] = useState(existing?.config?.apns_push_type ?? '')
  const [keyID, setKeyID] = useState('')
  const [teamID, setTeamID] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [status, setStatus] = useState('')

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setStatus('Проверяю ключ у Apple…')
    try {
      const payload: Record<string, unknown> = {
        enabled: true,
        config: { bundle_id: bundleID, default_env: env, apns_push_type: pushType },
        validate: Boolean(file),
      }
      if (file) {
        payload.credential = {
          kind: 'apns_p8',
          data_base64: await fileToBase64(file),
          key_id: keyID,
          team_id: teamID,
        }
      }
      await api.saveChannel(app.id, 'ios', payload)
      setStatus('Сохранено')
      onSaved()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Ошибка сохранения')
    }
  }

  return (
    <form className="card stack" onSubmit={save}>
      <h2>iOS — APNs</h2>
      <p className="muted">
        Ключ .p8 не истекает и работает для sandbox и production сразу. Окружение конкретного
        устройства определяется автоматически по test_type, который присылает SDK.
      </p>
      {pushType === 'voip' && (
        <p className="muted">
          Все рассылки приложения уходят как VoIP: apns-push-type voip, topic {bundleID || '<bundle id>'}.voip,
          высокий приоритет. Токены PushKit регистрируются отдельно от обычных — через POST
          /apps/&lt;app id&gt;/users с подпиской типа iOSPush.
        </p>
      )}
      <div className="grid">
        <label>
          Bundle ID
          <input value={bundleID} onChange={(e) => setBundleID(e.target.value)} placeholder="com.example.app" />
        </label>
        <label>
          Окружение по умолчанию
          <select value={env} onChange={(e) => setEnv(e.target.value)}>
            <option value="production">production</option>
            <option value="sandbox">sandbox</option>
          </select>
        </label>
        <label>
          Назначение приложения
          <select value={pushType} onChange={(e) => setPushType(e.target.value)}>
            <option value="">Обычные пуши</option>
            <option value="voip">VoIP-звонки (токены PushKit)</option>
          </select>
        </label>
        <label>
          Key ID
          <input value={keyID} onChange={(e) => setKeyID(e.target.value)} placeholder="ABC123DEFG" />
        </label>
        <label>
          Team ID
          <input value={teamID} onChange={(e) => setTeamID(e.target.value)} placeholder="TEAM123456" />
        </label>
        <label>
          Ключ .p8
          <input type="file" accept=".p8" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </label>
      </div>
      <CredentialState channel={existing} />
      <div className="row">
        <button className="primary" type="submit">
          Сохранить
        </button>
        {status && <span className="muted">{status}</span>}
      </div>
    </form>
  )
}

function WebChannel({ app, onSaved }: { app: AppDetailData; onSaved: () => void }) {
  const existing = channelOf(app, 'web')
  const [siteURL, setSiteURL] = useState(existing?.config?.site_url ?? '')
  const [status, setStatus] = useState('')

  async function save(event: React.FormEvent) {
    event.preventDefault()
    setStatus('Сохраняю…')
    try {
      await api.saveChannel(app.id, 'web', {
        enabled: true,
        config: { site_url: siteURL },
        credential: existing?.credential ? undefined : { kind: 'vapid' },
      })
      setStatus('Сохранено')
      onSaved()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Ошибка сохранения')
    }
  }

  return (
    <form className="card stack" onSubmit={save}>
      <h2>Web Push</h2>
      <p className="muted">Пара VAPID генерируется сервисом; публичный ключ отдаётся браузеру при подписке.</p>
      <div className="grid">
        <label>
          Адрес сайта
          <input value={siteURL} onChange={(e) => setSiteURL(e.target.value)} placeholder="https://example.com" />
        </label>
        {existing?.config?.vapid_public_key && (
          <label>
            VAPID public key
            <input readOnly value={existing.config.vapid_public_key} />
          </label>
        )}
      </div>
      {existing?.config?.vapid_public_key && <WebSnippet appId={app.id} />}
      <CredentialState channel={existing} />
      <div className="row">
        <button className="primary" type="submit">
          Сохранить
        </button>
        {status && <span className="muted">{status}</span>}
      </div>
    </form>
  )
}

// WebSnippet — как подключить web-SDK: скрипт на страницу и строка
// в service worker сайта.
function WebSnippet({ appId }: { appId: string }) {
  const origin = window.location.origin
  const page = `<script src="${origin}/sdk/web/v1/pushme-web.js"></script>
<script>
  PushMe.init({ appId: '${appId}', apiUrl: '${origin}' })
  PushMe.onSubscriptionChange((id) => { /* передать id своему бэкенду */ })
  PushMe.subscribe()
</script>`
  const worker = `// /pushme-sw.js в корне сайта
importScripts('${origin}/sdk/web/v1/pushme-sw.js')`
  return (
    <div className="stack">
      <p className="muted">Подключение на сайте:</p>
      <pre className="code">{page}</pre>
      <pre className="code">{worker}</pre>
    </div>
  )
}

function HuaweiChannel({ app, onSaved }: { app: AppDetailData; onSaved: () => void }) {
  const existing = channelOf(app, 'hms')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [appIdValue, setAppIdValue] = useState('')
  const [status, setStatus] = useState('')

  async function save(event: React.FormEvent) {
    event.preventDefault()
    const hasCredential = Boolean(clientId && clientSecret)
    setStatus(hasCredential ? 'Проверяю креды у Huawei…' : 'Сохраняю…')
    try {
      const payload: Record<string, unknown> = {
        enabled: true,
        config: {},
        validate: hasCredential,
      }
      if (hasCredential) {
        payload.credential = {
          kind: 'hms',
          client_id: clientId.trim(),
          client_secret: clientSecret.trim(),
          huawei_app_id: appIdValue.trim(),
        }
      }
      await api.saveChannel(app.id, 'hms', payload)
      setClientSecret('')
      setStatus('Сохранено')
      onSaved()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Ошибка сохранения')
    }
  }

  return (
    <form className="card stack" onSubmit={save}>
      <h2>Huawei — Push Kit</h2>
      <p className="muted">
        Client ID и Client Secret берутся в AppGallery Connect: проект → приложение →
        App Information. Client ID там же служит App ID и подставляется в адрес отправки.
      </p>
      <div className="grid">
        <label>
          Client ID
          <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="123456789" />
        </label>
        <label>
          Client Secret
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder="показывается один раз при сохранении"
          />
        </label>
        <label>
          App ID — если отличается от Client ID
          <input
            value={appIdValue}
            onChange={(e) => setAppIdValue(e.target.value)}
            placeholder="необязательно"
          />
        </label>
      </div>
      <CredentialState channel={existing} />
      <div className="row">
        <button className="primary" type="submit">
          Сохранить
        </button>
        {status && <span className="muted">{status}</span>}
      </div>
    </form>
  )
}

function CredentialState({ channel }: { channel?: { credential?: { kind: string; fingerprint: string; expires_at?: string } } }) {
  if (!channel?.credential) {
    return <p className="muted">Креды не загружены — отправка по этому каналу не работает.</p>
  }
  const { kind, fingerprint, expires_at } = channel.credential
  return (
    <p className="muted">
      Загружены креды <span className="badge ok">{kind}</span> <code>{fingerprint}</code>
      {expires_at ? ` · истекают ${new Date(expires_at).toLocaleDateString()}` : ' · без срока истечения'}
    </p>
  )
}

function Keys({
  app,
  onAppIdChanged,
  onDeleted,
}: {
  app: AppDetailData
  onAppIdChanged: (id: string) => void
  onDeleted: () => void
}) {
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [fresh, setFresh] = useState('')
  const [error, setError] = useState('')

  const reload = useCallback(async () => {
    const { keys } = await api.keys(app.id)
    setKeys(keys ?? [])
  }, [app.id])

  useEffect(() => {
    void reload().catch(() => undefined)
  }, [reload])

  async function create() {
    try {
      const { key } = await api.createKey(app.id, 'api key')
      setFresh(key)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать ключ')
    }
  }

  async function revoke(key: ApiKey) {
    if (
      !confirm(
        `Отозвать ключ ${key.prefix}…?\n\n` +
          'Интеграции, которые им пользуются, сразу начнут получать 401. ' +
          'Вернуть ключ нельзя — только выпустить новый.',
      )
    ) {
      return
    }
    try {
      await api.revokeKey(app.id, key.id)
      await reload()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отозвать ключ')
    }
  }

  const snippet = `// Android, OneSignal SDK 5.x
OneSignal.initWithContext(context, "${app.id}")
// Базовый URL SDK переопределяется инструментом подмены хоста,
// см. docs/06-client-sdk.md`

  return (
    <div className="stack">
      <div className="card stack">
        <h2>Идентификатор приложения</h2>
        <AppIdEditor app={app} onAppIdChanged={onAppIdChanged} />
        <pre className="mono" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
          {snippet}
        </pre>
      </div>

      <div className="card stack">
        <h2>API-ключи</h2>
        {fresh && (
          <div className="notice secret">
            Новый ключ показывается один раз: <code>{fresh}</code>
          </div>
        )}
        <p className="muted">
          Ключ хранится только хешем и показывается один раз при создании.
          Потерянный ключ не восстанавливают — его отзывают и выпускают новый.
        </p>
        {error && <div className="notice error">{error}</div>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Префикс</th>
                <th>Создан</th>
                <th>Использовался</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="mono">{k.prefix}…</td>
                  <td className="muted">{new Date(k.created_at).toLocaleDateString('ru-RU')}</td>
                  <td className="muted">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString('ru-RU') : 'ни разу'}
                  </td>
                  <td>
                    <button className="link" onClick={() => revoke(k)}>
                      Отозвать
                    </button>
                  </td>
                </tr>
              ))}
              {keys.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Ключей нет — отправлять рассылки сейчас нечем.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="row">
          <button onClick={create}>Создать ключ</button>
        </div>
      </div>

      <DeleteApp app={app} onDeleted={onDeleted} />
    </div>
  )
}

// Удаление приложения необратимо и уносит аудиторию вместе с историей,
// поэтому подтверждается названием, а не кнопкой «вы уверены».
function DeleteApp({ app, onDeleted }: { app: AppDetailData; onDeleted: () => void }) {
  const [open, setOpen] = useState(false)
  const [confirmName, setConfirmName] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.deleteApp(app.id, confirmName.trim())
      onDeleted()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось удалить приложение')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card stack danger">
      <h2>Удаление приложения</h2>
      <p className="muted">
        Вместе с приложением удаляются подписки, рассылки, журнал доставки, креды платформ
        и API-ключи. Устройства перестанут регистрироваться: SDK начнёт получать 404 на
        запрос настроек. Восстановить нельзя.
      </p>

      {!open ? (
        <div className="row">
          <button onClick={() => setOpen(true)}>Удалить приложение</button>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          <label>
            Введите название приложения — <strong>{app.name}</strong> — чтобы подтвердить
            <input
              autoFocus
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={app.name}
            />
          </label>
          {error && <div className="notice error">{error}</div>}
          <div className="row">
            <button type="submit" className="danger" disabled={busy || confirmName.trim() !== app.name}>
              Удалить навсегда
            </button>
            <button type="button" onClick={() => setOpen(false)}>
              Отмена
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

// Смена App ID нужна при переезде: в установленных клиентах идентификатор
// уже зашит. Операция переносит вместе с приложением всю аудиторию,
// рассылки и статистику, но обрывает клиентов со старым идентификатором —
// поэтому она за отдельным подтверждением.
function AppIdEditor({
  app,
  onAppIdChanged,
}: {
  app: AppDetailData
  onAppIdChanged: (id: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(app.id)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const next = value.trim()
    if (next === app.id) {
      setEditing(false)
      return
    }
    if (
      !confirm(
        `Сменить App ID на ${next}?\n\n` +
          'Аудитория, рассылки, креды и статистика переедут вместе с приложением. ' +
          'Клиенты, собранные со старым идентификатором, перестанут регистрироваться, ' +
          'пока их не обновят.',
      )
    ) {
      return
    }

    setBusy(true)
    setError('')
    try {
      const result = await api.changeAppId(app.id, next)
      setEditing(false)
      onAppIdChanged(result.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сменить идентификатор')
    } finally {
      setBusy(false)
    }
  }

  if (!editing) {
    return (
      <div className="stack">
        <label>
          App ID
          <input readOnly value={app.id} onFocus={(e) => e.currentTarget.select()} />
        </label>
        <div className="row">
          <button
            className="link"
            onClick={() => {
              setValue(app.id)
              setEditing(true)
            }}
          >
            Сменить идентификатор
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            нужно при переезде, когда App ID уже зашит в установленных клиентах
          </span>
        </div>
      </div>
    )
  }

  return (
    <form className="stack" onSubmit={submit}>
      <label>
        Новый App ID
        <div className="row">
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="UUID — именно такой формат проверяет SDK"
            style={{ flex: 1, minWidth: 260 }}
          />
          <button type="button" onClick={() => setValue(crypto.randomUUID())}>
            Сгенерировать
          </button>
        </div>
      </label>
      {error && <div className="notice error">{error}</div>}
      <div className="notice">
        Аудитория, рассылки, креды и статистика переедут вместе с приложением. Клиенты со старым
        идентификатором перестанут регистрироваться, пока их не обновят.
      </div>
      <div className="row">
        <button className="primary" type="submit" disabled={busy}>
          Сменить
        </button>
        <button type="button" onClick={() => setEditing(false)}>
          Отмена
        </button>
      </div>
    </form>
  )
}

function DiagnosticsTab({ appId }: { appId: string }) {
  const [data, setData] = useState<Diagnostics | null>(null)

  useEffect(() => {
    api.diagnostics(appId).then(setData).catch(() => undefined)
  }, [appId])

  if (!data) return <p className="muted">Загрузка…</p>

  return (
    <div className="stack">
      <div className="card">
        <h2>Состояние каналов</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Канал</th>
                <th>Креды</th>
                <th>Истекают</th>
              </tr>
            </thead>
            <tbody>
              {data.credentials.map((c) => (
                <tr key={c.channel}>
                  <td>{c.channel}</td>
                  <td>
                    {c.has_credential ? (
                      <span className="badge ok">{c.kind}</span>
                    ) : (
                      <span className="badge bad">не загружены</span>
                    )}
                  </td>
                  <td>{c.days_left !== undefined ? `через ${c.days_left} дн.` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.hints.length > 0 && (
        <div className="card stack">
          <h2>Что это значит</h2>
          {data.hints.map((hint) => (
            <div className="notice" key={hint}>
              {hint}
            </div>
          ))}
        </div>
      )}

      <div className="card table-wrap">
        <h2>Ошибки провайдеров за сутки</h2>
        <table>
          <thead>
            <tr>
              <th>Провайдер</th>
              <th>Код</th>
              <th>Ответ</th>
              <th>Количество</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_errors.map((e, i) => (
              <tr key={i}>
                <td>{e.provider}</td>
                <td className="mono">{e.code}</td>
                <td className="muted">{e.raw}</td>
                <td>{e.count}</td>
              </tr>
            ))}
            {data.recent_errors.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  Ошибок нет.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
